import { ReactElement, ReactNode, useState } from "react";

import "@fontsource-variable/open-sans";
import "@fontsource-variable/spline-sans";
import { BiBody, BiCreditCard, BiHome, BiLogOut, BiRecycle } from "react-icons/bi";
import { BsCaretRightFill } from "react-icons/bs";
import { PiDotsNine, PiDotsSix, PiDotsThree, PiMapPinFill } from "react-icons/pi";
import { Outlet, useMatch, useNavigate } from "react-router";

import { Box, Flex, Grid, HStack, Stack, Text, createListCollection } from "@chakra-ui/react";

import { Provider as ChakraProvider } from "@/codegen/ui/provider";
import {
  SelectContent,
  SelectItem,
  SelectLabel,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from "@/codegen/ui/select";
import { Toaster } from "@/codegen/ui/toaster";
import { usePermerRequirements } from "@/hooks/useHasPermission";

import { AppConfig } from "../app-config";
import { JOYA_LEGAL_ENTITY_UUID, OUIHELP_LEGAL_ENTITY_UUID } from "../known-legal-entity-uuids";

import { LegalEntityContext } from "./legal-entity-context";

const entities = createListCollection({
  items: [
    { label: "Groupe Ouihelp", value: "" },
    { label: "Ouihelp", value: OUIHELP_LEGAL_ENTITY_UUID },
    { label: "Joya", value: JOYA_LEGAL_ENTITY_UUID },
  ],
});

interface NavGroupProps {
  label: string;
  children: ReactNode;
}

interface NavItemProps {
  href?: string;
  label: string;
  subtle?: boolean;
  active?: boolean;
  icon?: ReactElement;
  endElement?: ReactElement;
  children?: ReactNode;
  linkTo?: string;
  onClick?: () => void;
}

const NavItem = (props: NavItemProps) => {
  const navigate = useNavigate();
  const { active, subtle, icon, children, label, endElement, href, linkTo, onClick } = props;
  const match = useMatch(linkTo || "PROBABLY_NOT");
  const refinedActive = active || match;
  return (
    <HStack
      asChild
      w="full"
      px="3"
      py="2"
      cursor="pointer"
      userSelect="none"
      rounded="md"
      transition="all 0.2s"
      bg={refinedActive ? "gray.700" : undefined}
      _hover={{ bg: "gray.700" }}
      _active={{ bg: "gray.600" }}
      onClick={
        onClick
          ? onClick
          : () => {
              if (linkTo) {
                void navigate(linkTo);
              }
            }
      }
    >
      <a href={href}>
        {icon && (
          <Box fontSize="lg" color={refinedActive ? "currentcolor" : "gray.400"}>
            {icon}
          </Box>
        )}
        <Box
          flex="1"
          fontWeight="inherit"
          color={subtle ? "gray.400" : undefined}
          pl={!icon ? "26px" : undefined}
        >
          {label}
        </Box>
        {endElement && !children && <Box>{endElement}</Box>}
        {children && <Box fontSize="xs" flexShrink={0} as={BsCaretRightFill} />}
      </a>
    </HStack>
  );
};

const NavGroup = (props: NavGroupProps) => {
  const { label, children } = props;
  return (
    <Box>
      <Text
        px="3"
        fontSize="xs"
        fontWeight="semibold"
        textTransform="uppercase"
        letterSpacing="widest"
        color="gray.500"
        mb="3"
      >
        {label}
      </Text>
      <Stack gap="1">{children}</Stack>
    </Box>
  );
};

export const OuterShell: React.FC<React.PropsWithChildren> = ({
  children,
}: React.PropsWithChildren) => <ChakraProvider enableSystem={false}>{children}</ChakraProvider>;

export const Shell = () => {
  return (
    <OuterShell>
      <Outlet />
    </OuterShell>
  );
};

export const ShellWithSidebar = () => {
  const [legalEntityContext, setLegalEntityContext] = useState([""]);
  const permer = usePermerRequirements();

  const isDSI = permer.hasDsitAdminDsi();
  const isTeamDSIT =
    AppConfig.publicClientApplication.getActiveAccount()?.username === "bastien@ouihelp.fr";

  return (
    <Grid
      width="100vw"
      maxWidth="100%"
      minHeight="100vh"
      gridTemplateColumns={"256px 1fr"}
      gridTemplateRows={"1fr"}
    >
      <Box w="64" bg="gray.900" color="white">
        <Flex h="full" direction="column" px="4" py="4">
          <Stack gap="8" flex="1" overflow="auto" pt="8">
            <NavGroup label="Filtre entité">
              <SelectRoot
                collection={entities}
                value={legalEntityContext}
                onValueChange={e => setLegalEntityContext(e.value)}
              >
                <SelectLabel />
                <SelectTrigger>
                  <SelectValueText />
                </SelectTrigger>
                <SelectContent>
                  {entities.items.map(e => (
                    <SelectItem item={e} key={e.value}>
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </NavGroup>

            <NavGroup label="Collaborateurs">
              <NavItem icon={<BiBody />} label="Collaborateurs" linkTo="/2/admin/hohishes" />
            </NavGroup>

            <NavGroup label="Géographies">
              <NavItem
                icon={<PiDotsNine />}
                label="Régions"
                linkTo="/2/admin/geographies/regions"
              />
              <NavItem icon={<PiDotsSix />} label="Zones" linkTo="/2/admin/geographies/zones" />
              <NavItem
                icon={<PiDotsThree />}
                label="Agences"
                linkTo="/2/admin/geographies/agencies"
              />
              <NavItem
                icon={<PiMapPinFill />}
                label="Codes postaux"
                linkTo="/2/admin/geographies/postal-codes"
              />
            </NavGroup>

            <NavGroup label="Gestion">
              <NavItem
                icon={<PiMapPinFill />}
                label="Aides financières"
                linkTo="/1/financial-helps"
              />
            </NavGroup>

            {isDSI ? (
              <NavGroup label="DSI">
                <NavItem
                  icon={<BiCreditCard />}
                  label="Erreurs SI"
                  linkTo="/2/admin/dsi/third-parties-errors"
                />
              </NavGroup>
            ) : null}

            {isTeamDSIT ? (
              <NavGroup label="Team DSIT">
                <NavItem
                  icon={<BiRecycle />}
                  label="Trigger Sentry (onClick)"
                  // @ts-expect-error: Intentional to trigger Sentry.
                  onClick={() => iDoNotExist()}
                />
                <NavItem
                  icon={<BiRecycle />}
                  label="Trigger Sentry (Render)"
                  linkTo="/2/admin/test-error"
                />
              </NavGroup>
            ) : null}
          </Stack>

          <Box>
            <NavItem subtle icon={<BiHome />} label="Retour à l'accueil" linkTo="/" />
            <NavItem subtle icon={<BiLogOut />} label="Se déconnecter" onClick={AppConfig.logout} />
          </Box>
        </Flex>
      </Box>

      <Box width={"100%"}>
        <LegalEntityContext.Provider value={legalEntityContext[0]}>
          <Outlet />
          <Toaster />
        </LegalEntityContext.Provider>
      </Box>
    </Grid>
  );
};
